// Submodule imports keep the bundle to HMAC-SHA256 + encoders — a bare
// 'crypto-js' import pulls all 35 algorithm modules (~235KB) into _app.
import Base64 from 'crypto-js/enc-base64'
import Hex from 'crypto-js/enc-hex'
import Utf8 from 'crypto-js/enc-utf8'
import HmacSHA256 from 'crypto-js/hmac-sha256'
import { Project } from 'common/project'

const KEY = '686f78746f6e6d69782d696d6167652d70726f7879'
const SALT = '32587a4879754a347a4c406662506f714624334a'
const urlSafeBase64 = (string: string) => {
  return btoa(string).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_')
}

const sign = (salt: string, target: string, secret: string) => {
  // HMAC over salt-bytes ++ utf8(target) — byte-identical to the previous
  // incremental hmac.update(salt); hmac.update(target) sequence.
  const message = Hex.parse(salt).concat(Utf8.parse(target))
  return Base64.stringify(HmacSHA256(message, Hex.parse(secret)))
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
}

const normalizeUrl = (inputUrl: string): string => {
  let url = inputUrl

  // Ensure URL starts with /
  if (
    !url.startsWith('/') &&
    !url.startsWith('http') &&
    !url.startsWith('www')
  ) {
    url = `/${url}`
  }

  return url
}

const shouldUseImageProxy = (url: string, debug: boolean): boolean => {
  return !(
    !Project.imageProxy ||
    url.includes(Project.imageProxy) ||
    __DEV__ ||
    debug ||
    process.env.NEXT_PUBLIC_DISABLE_IMAGE_PROXY === 'true'
  )
}

const getServerSideUrl = (url: string): string => {
  let siteUrl = process.env.NEXT_PUBLIC_SITE_URL

  if (!siteUrl) {
    siteUrl = constructVercelUrl() || 'https://www.hoxtonmix.com'
  }

  return `${siteUrl}${url}`
}

const constructVercelUrl = (): string | null => {
  const branch = process.env.NEXT_PUBLIC_BRANCH_NAME
  const projectName = process.env.NEXT_PUBLIC_PROJECT_NAME

  if (branch && projectName && process.env.VERCEL_ENV === 'preview') {
    const alias = `${projectName}-${branch}`
      .replace(/_/g, '-')
      .replace(/\//g, '-')
      .replace(/\./g, '-')
    const constructedUrl = `https://${alias}.vercel.app`
    return constructedUrl
  }

  if (process.env.NEXT_PUBLIC_VERCEL_URL) {
    return `https://${process.env.NEXT_PUBLIC_VERCEL_URL}`
  }

  return null
}

const buildFullUrl = (url: string): string => {
  const isClientSide = typeof window !== 'undefined'
  const isRelativeUrl = !url.startsWith('http') && !url.startsWith('www')

  if (isClientSide && isRelativeUrl) {
    return document.location.origin + url
  }

  if (!isClientSide && isRelativeUrl) {
    return getServerSideUrl(url)
  }

  return url
}

export default function imageResize(
  urlInput: string | { default: { src: string } },
  width: number | null = null,
  height: number | null = null,
  format = 'webp',
  debug = false,
  dprOverride?: number,
) {
  // Extract URL from input
  const extractedUrl =
    typeof urlInput === 'string' ? urlInput : urlInput?.default?.src
  if (!extractedUrl) return extractedUrl

  // Normalize and clean URL
  let url = normalizeUrl(extractedUrl)

  // Early return if image proxy shouldn't be used
  if (!shouldUseImageProxy(url, debug)) {
    return url
  }

  // Determine final format (SVGs stay as SVG)
  const finalFormat = url.endsWith('.svg') ? 'svg' : format

  // Build full URL
  url = buildFullUrl(url)

  // Add cache-busting parameter from build
  const cacheVersion = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA

  if (cacheVersion) {
    const separator = url.includes('?') ? '&' : '?'
    url = `${url}${separator}v=${cacheVersion}`
  }

  // Generate image proxy URL
  const encodedUrl = urlSafeBase64(url)
  const devicePixelRatio =
    dprOverride ??
    (typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1)

  // `resizing_type:fit` (not `fill`) so the proxy never crops the source: it
  // scales the image to fit inside the box while preserving its true aspect
  // ratio. Visual framing is owned by CSS (`object-fit`) at display time, which
  // is identical in dev and prod. `fill` cropped to the declared box ratio —
  // and because `__DEV__` bypasses the proxy entirely, that crop only ever
  // appeared in prod, so any declared-dimension/asset-ratio mismatch silently
  // cut the image on prod while looking fine locally. See the ratio audit in
  // scripts/audit-image-dimensions.mjs + its guard test.
  const processingOptions = width
    ? `/size:${parseInt(`${width}`)}:${parseInt(
        `${height}`,
      )}/resizing_type:fit/dpr:${devicePixelRatio}/format:${finalFormat}/`
    : `/resizing_type:fit/dpr:${devicePixelRatio}/format:${finalFormat}/`

  const path = `${processingOptions}${encodedUrl}.${finalFormat}`
  const signature = sign(SALT, path, KEY)

  return `${Project.imageProxy}${signature}${path}`
}

export function generateSrcSet(
  _url: string | { default: { src: string } },
  width: number,
  height: number,
  format = 'webp',
  dpiSteps: number[] = [1, 2],
) {
  if (typeof _url === 'string' && _url.includes('.svg')) {
    return {
      src: _url,
      srcSet: '',
    }
  }

  return {
    src: imageResize(_url, width, height, format, false, 1),
    srcSet: dpiSteps
      .map((dpi) => {
        const url = imageResize(_url, width, height, format, false, dpi)
        return `${url} ${dpi}x`
      })
      .join(', '),
  }
}

export function generatePictureSources(
  _url: string | { default: { src: string } },
  width: number,
  height: number,
  dpiSteps: number[] = [1, 2],
) {
  if (typeof _url === 'string' && _url.includes('.svg')) {
    return {
      sources: [] as { type: string; srcSet: string }[],
      src: _url,
      srcSet: '',
    }
  }

  const formats = [
    { format: 'avif', type: 'image/avif' },
    { format: 'webp', type: 'image/webp' },
  ]

  const sources = formats.map(({ format, type }) => ({
    srcSet: dpiSteps
      .map(
        (dpi) =>
          `${imageResize(_url, width, height, format, false, dpi)} ${dpi}x`,
      )
      .join(', '),
    type,
  }))

  const fallback = generateSrcSet(_url, width, height, 'webp', dpiSteps)

  return {
    ...fallback,
    sources,
  }
}
